// src/pages/employer/profile/EmployerMedia.jsx
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../../../context/AuthContext';
import { useConfirm, EditBtn, DelBtn, AddBtn, usePersistedState } from '../../../components/shared/UI';
import api, { snippetAPI, mediaAPI } from '../../../services/api';
import { Image, FileText, Upload, Trash2, Pencil, Bold, Italic, List, ListOrdered, X, ChevronUp, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';

// ─── Media section config ──────────────────────────────────

const MEDIA_SECTIONS = [
  {
    key:    'PHOTO',
    label:  'Images',
    accept: 'image/jpeg,image/png,image/webp,image/svg+xml',
    icon:   Image,
    hint:   'JPEG, PNG, WebP or SVG',
    isDoc:  false,
  },
  {
    key:    'APPLICATION_FORM',
    label:  'Application Forms',
    accept: '.pdf,.doc,.docx,.rtf,.odt',
    icon:   FileText,
    hint:   'PDF or Word document',
    isDoc:  true,
  },
  {
    key:    'JOB_DESCRIPTION',
    label:  'Full Job Descriptions',
    accept: '.pdf,.doc,.docx,.rtf,.odt',
    icon:   FileText,
    hint:   'PDF or Word document',
    isDoc:  true,
  },
];

// ─── Collapsible section wrapper (persisted state) ─────────
// Used for each media category and the text-snippets section.

const CollapsibleLibrarySection = ({ storageKey, title, headerRight, children, defaultOpen = true }) => {
  const [open, setOpen] = usePersistedState(storageKey, defaultOpen);
  return (
    <div className="section-box" style={{ marginBottom: 16 }}>
      <div
        className="section-box-header"
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          {title}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} onClick={e => e.stopPropagation()}>
          {headerRight}
          <span onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', marginLeft: 4, cursor: 'pointer' }}>
            {open
              ? <ChevronUp size={14} style={{ color: 'rgba(255,255,255,0.85)' }} />
              : <ChevronDown size={14} style={{ color: 'rgba(255,255,255,0.85)' }} />}
          </span>
        </div>
      </div>
      {open && <div className="section-box-body">{children}</div>}
    </div>
  );
};

// ─── Name prompt modal (used on upload + rename) ───────────

const NamePromptModal = ({ title, initial, fileName, onSave, onCancel }) => {
  const [value, setValue] = useState(initial || '');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await onSave(trimmed);
    } finally { setSaving(false); }
  };

  return ReactDOM.createPortal(
    <div className="modal-overlay" style={{ zIndex: 1100 }}>
      <div className="modal-box" style={{ maxWidth: 460, width: '92vw' }}>
        <div className="modal-header">
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{title}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onCancel} style={{ padding: '4px 6px' }}>
            <X size={15} />
          </button>
        </div>
        <div className="modal-body" style={{ padding: '20px 24px' }}>
          {fileName && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
              File: <span style={{ color: 'var(--text)' }}>{fileName}</span>
            </div>
          )}
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
            Document Name *
          </label>
          <input
            type="text"
            value={value}
            autoFocus
            onChange={e => setValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
            placeholder="e.g. Senior Accountant — Application Form"
            style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, background: 'white', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
          />
        </div>
        <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={submit} disabled={saving || !value.trim()}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── Upload button ─────────────────────────────────────────

const UploadBtn = ({ sectionKey, accept, disabled, isUploading, onFilePicked }) => {
  const fileRef = useRef();

  const handleFile = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    onFilePicked(file, sectionKey);
  };

  return (
    <>
      <button
        className="btn btn-outline btn-sm"
        onClick={() => fileRef.current.click()}
        disabled={disabled}
        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
      >
        <Upload size={13} />
        {isUploading ? 'Uploading…' : 'Upload'}
      </button>
      <input ref={fileRef} type="file" accept={accept} style={{ display: 'none' }} onChange={handleFile} />
    </>
  );
};

// ─── File list ─────────────────────────────────────────────

const FileList = ({ items, isDoc, onDelete, onRename, isAdmin }) => {
  if (!items.length) return (
    <p style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic', margin: 0, padding: '4px 0' }}>
      Nothing uploaded yet.
    </p>
  );

  if (isDoc) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.map((m) => (
          <div key={m.id} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 12px', borderRadius: 6,
            background: '#F8F9FA', border: '1px solid var(--border)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <FileText size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
              <div style={{ minWidth: 0 }}>
                <a href={m.url} target="_blank" rel="noopener noreferrer" style={{
                  fontSize: 13, color: 'var(--secondary)', fontWeight: 500,
                  textDecoration: 'none', display: 'block',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
                  onMouseEnter={(e) => { e.target.style.textDecoration = 'underline'; }}
                  onMouseLeave={(e) => { e.target.style.textDecoration = 'none'; }}
                >
                  {m.name}
                </a>
                {m.fileSize && (
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {(m.fileSize / 1024).toFixed(0)} KB
                  </span>
                )}
              </div>
            </div>
            {isAdmin && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                <button onClick={() => onRename(m)} title="Rename" style={{
                  background: 'none', border: 'none', cursor: 'pointer', color: 'var(--secondary)',
                  display: 'flex', alignItems: 'center', padding: '2px 4px',
                }}>
                  <Pencil size={14} />
                </button>
                <button onClick={() => onDelete(m.id)} title="Delete" style={{
                  background: 'none', border: 'none', cursor: 'pointer', color: '#CC0000',
                  display: 'flex', alignItems: 'center', padding: '2px 4px',
                }}>
                  <Trash2 size={14} />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
      {items.map((m) => (
        <div key={m.id} style={{
          borderRadius: 8, border: '1px solid var(--border)', overflow: 'hidden',
          background: '#F8F9FA', position: 'relative',
        }}>
          <img src={m.url} alt={m.name}
            style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }} />
          <div style={{ padding: '6px 8px' }}>
            <div style={{ fontSize: 11, color: 'var(--text)', fontWeight: 500,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {m.name}
            </div>
            {m.fileSize && (
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                {(m.fileSize / 1024).toFixed(0)} KB
              </div>
            )}
          </div>
          {isAdmin && (
            <div style={{
              position: 'absolute', top: 6, right: 6,
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              <button onClick={() => onRename(m)} title="Rename" style={{
                background: 'rgba(0,0,0,0.55)', border: 'none', borderRadius: 4,
                padding: '3px 5px', cursor: 'pointer', display: 'flex', alignItems: 'center',
              }}>
                <Pencil size={11} style={{ color: 'white' }} />
              </button>
              <button onClick={() => onDelete(m.id)} title="Delete" style={{
                background: 'rgba(0,0,0,0.55)', border: 'none', borderRadius: 4,
                padding: '3px 5px', cursor: 'pointer', display: 'flex', alignItems: 'center',
              }}>
                <Trash2 size={11} style={{ color: 'white' }} />
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ─── Snippet editor modal ──────────────────────────────────

const SnippetModal = ({ snippet, onSave, onCancel }) => {
  const [name, setName]     = useState(snippet?.name || '');
  const [saving, setSaving] = useState(false);
  const editorRef           = useRef(null);
  const savedRangeRef       = useRef(null);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.innerHTML = snippet?.content || '';
    editor.focus();
  }, []); // eslint-disable-line

  useEffect(() => {
    const editor = editorRef.current;

    const onSelChange = () => {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const r = sel.getRangeAt(0);
        if (editor && editor.contains(r.commonAncestorContainer)) {
          savedRangeRef.current = r.cloneRange();
        }
      }
    };

    const onDocMousedown = (e) => {
      const btn = e.target.closest('[data-snip-cmd]');
      if (!btn) return;
      e.preventDefault();
      const ed = editorRef.current;
      if (ed) {
        ed.focus();
        if (savedRangeRef.current) {
          try {
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(savedRangeRef.current);
          } catch (_) {}
        }
      }
      const cmd = btn.getAttribute('data-snip-cmd');
      if (cmd === 'bold')   document.execCommand('bold',                false, null);
      if (cmd === 'italic') document.execCommand('italic',              false, null);
      if (cmd === 'ul')     document.execCommand('insertUnorderedList', false, null);
      if (cmd === 'ol')     document.execCommand('insertOrderedList',   false, null);
    };

    document.addEventListener('selectionchange', onSelChange);
    document.addEventListener('mousedown', onDocMousedown);
    return () => {
      document.removeEventListener('selectionchange', onSelChange);
      document.removeEventListener('mousedown', onDocMousedown);
    };
  }, []); // eslint-disable-line

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onSave({ name: name.trim(), content: editorRef.current?.innerHTML || '' });
    } finally { setSaving(false); }
  };

  const tbStyle = {
    background: 'none', border: '1px solid #ddd', borderRadius: 3,
    padding: '4px 7px', cursor: 'pointer', display: 'flex', alignItems: 'center',
    color: '#333', lineHeight: 1,
  };
  const sep = <div style={{ width: 1, height: 20, background: '#ddd', margin: '0 2px' }} />;

  return ReactDOM.createPortal(
    <div className="modal-overlay" style={{ zIndex: 1000 }}>
      <div className="modal-box" style={{ maxWidth: 580, width: '94vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>
            {snippet ? 'Edit Snippet' : 'New Snippet'}
          </h3>
          <button className="btn btn-ghost btn-sm" onClick={onCancel} style={{ padding: '4px 6px' }}>
            <X size={15} />
          </button>
        </div>
        <div className="modal-body" style={{ flex: 1, overflowY: 'auto', padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
              Snippet Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              autoFocus
              placeholder="e.g. Standard closing paragraph"
              style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, background: 'white', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
              Content
            </label>
            <div style={{ display: 'flex', gap: 5, alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: 8, marginBottom: 8 }}>
              <button type="button" title="Bold"          data-snip-cmd="bold"   style={tbStyle}><Bold size={14} /></button>
              <button type="button" title="Italic"        data-snip-cmd="italic" style={tbStyle}><Italic size={14} /></button>
              {sep}
              <button type="button" title="Bullet list"   data-snip-cmd="ul"     style={tbStyle}><List size={14} /></button>
              <button type="button" title="Numbered list" data-snip-cmd="ol"     style={tbStyle}><ListOrdered size={14} /></button>
            </div>
            <div
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning
              className="rte-editor"
              style={{
                minHeight: 180, border: '1px solid var(--border)', borderRadius: 4,
                padding: '10px 12px', fontSize: 13, lineHeight: 1.65,
                outline: 'none', background: 'white',
                fontFamily: 'inherit',
              }}
            />
          </div>
        </div>
        <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !name.trim()}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── Main page ─────────────────────────────────────────────

export const EmployerMedia = () => {
  const { activeOrg, activeProfile, isAdmin } = useAuth();
  const { confirm, dialog }                   = useConfirm();
  const [media, setMedia]                     = useState([]);
  const [snippets, setSnippets]               = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [uploading, setUploading]             = useState(null);
  const [snippetModal, setSnippetModal]       = useState(null); // null | { mode: 'create' } | { mode: 'edit', snippet }
  const [pendingUpload, setPendingUpload]     = useState(null); // { file, sectionKey } — waiting for name
  const [renameTarget, setRenameTarget]       = useState(null); // media item being renamed

  const orgId     = activeOrg?.id;
  const profileId = activeProfile?.id;

  const loadAll = () => {
    if (!orgId || !profileId) { setLoading(false); return; }
    Promise.all([
      api.get(`/orgs/${orgId}/profiles/${profileId}/media`),
      snippetAPI.getSnippets(orgId, profileId),
    ])
      .then(([mediaRes, snipRes]) => {
        setMedia(mediaRes.data.data.media || []);
        setSnippets(snipRes.data.data.snippets || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadAll(); }, [orgId, profileId]); // eslint-disable-line

  const handleFilePicked = (file, sectionKey) => {
    setPendingUpload({ file, sectionKey });
  };

  const handleConfirmUpload = async (name) => {
    if (!pendingUpload) return;
    const { file, sectionKey } = pendingUpload;
    setUploading(sectionKey);
    try {
      await mediaAPI.upload(orgId, profileId, file, sectionKey, name);
      toast.success('Uploaded');
      setPendingUpload(null);
      loadAll();
    } catch {
      // axios interceptor handles error toast
    } finally {
      setUploading(null);
    }
  };

  const handleRename = async (name) => {
    if (!renameTarget) return;
    try {
      const { data } = await mediaAPI.rename(orgId, profileId, renameTarget.id, name);
      setMedia(prev => prev.map(m => m.id === renameTarget.id ? data.data.media : m));
      setRenameTarget(null);
      toast.success('Renamed');
    } catch {
      // axios interceptor handles error toast
    }
  };

  const handleDeleteMedia = async (mediaId, name) => {
    const ok = await confirm('Delete File', `Delete "${name}"? This cannot be undone.`);
    if (!ok) return;
    try {
      await api.delete(`/orgs/${orgId}/profiles/${profileId}/media/${mediaId}`);
      toast.success('Deleted');
      setMedia((m) => m.filter((x) => x.id !== mediaId));
    } catch {
      // axios interceptor handles error toast
    }
  };

  const handleSaveSnippet = async ({ name, content }) => {
    if (snippetModal?.mode === 'edit') {
      const { data } = await snippetAPI.updateSnippet(orgId, profileId, snippetModal.snippet.id, { name, content });
      setSnippets(prev =>
        prev.map(s => s.id === snippetModal.snippet.id ? data.data.snippet : s)
            .sort((a, b) => a.name.localeCompare(b.name))
      );
    } else {
      const { data } = await snippetAPI.createSnippet(orgId, profileId, { name, content });
      setSnippets(prev => [...prev, data.data.snippet].sort((a, b) => a.name.localeCompare(b.name)));
    }
    setSnippetModal(null);
  };

  const handleDeleteSnippet = async (snippet) => {
    const ok = await confirm('Delete Snippet', `Delete "${snippet.name}"? This cannot be undone.`);
    if (!ok) return;
    try {
      await snippetAPI.deleteSnippet(orgId, profileId, snippet.id);
      setSnippets(prev => prev.filter(s => s.id !== snippet.id));
    } catch {
      // axios interceptor handles error toast
    }
  };

  if (!activeOrg) return (
    <div className="fade-in">
      <div className="page-header"><h1>Content Library</h1></div>
      <div style={{ padding: 40, color: 'var(--text-muted)', textAlign: 'center' }}>No active profile selected.</div>
    </div>
  );

  if (loading) return (
    <div className="fade-in">
      <div className="page-header"><h1>Content Library</h1></div>
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
    </div>
  );

  return (
    <div className="fade-in">
      {dialog}
      {snippetModal && (
        <SnippetModal
          snippet={snippetModal.mode === 'edit' ? snippetModal.snippet : null}
          onSave={handleSaveSnippet}
          onCancel={() => setSnippetModal(null)}
        />
      )}

      {pendingUpload && (
        <NamePromptModal
          title="Name this document"
          initial={pendingUpload.file.name.replace(/\.[^./]+$/, '')}
          fileName={pendingUpload.file.name}
          onSave={handleConfirmUpload}
          onCancel={() => setPendingUpload(null)}
        />
      )}

      {renameTarget && (
        <NamePromptModal
          title="Rename"
          initial={renameTarget.name}
          onSave={handleRename}
          onCancel={() => setRenameTarget(null)}
        />
      )}

      <div className="page-header">
        <h1>Content Library</h1>
      </div>

      {/* ── Media sections ── */}
      {MEDIA_SECTIONS.map(({ key, label, accept, icon: Icon, hint, isDoc }) => {
        const items = media.filter((m) => m.type === key);
        return (
          <CollapsibleLibrarySection
            key={key}
            storageKey={`media.section.${key}.open`}
            title={<><Icon size={14} />{label}{items.length > 0 && (
              <span style={{ fontSize: 11, fontWeight: 400, color: 'rgba(255,255,255,0.7)', marginLeft: 2 }}>
                ({items.length})
              </span>
            )}</>}
            headerRight={isAdmin && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>{hint}</span>
                <UploadBtn
                  sectionKey={key}
                  accept={accept}
                  disabled={!!uploading || !!pendingUpload}
                  isUploading={uploading === key}
                  onFilePicked={handleFilePicked}
                />
              </div>
            )}
          >
            <FileList
              items={items}
              isDoc={isDoc}
              onDelete={(id) => handleDeleteMedia(id, items.find(m => m.id === id)?.name || 'this file')}
              onRename={setRenameTarget}
              isAdmin={isAdmin}
            />
          </CollapsibleLibrarySection>
        );
      })}

      {/* ── Text Snippets ── */}
      <CollapsibleLibrarySection
        storageKey="media.section.SNIPPETS.open"
        title={<><FileText size={14} />Text Snippets{snippets.length > 0 && (
          <span style={{ fontSize: 11, fontWeight: 400, color: 'rgba(255,255,255,0.7)', marginLeft: 2 }}>
            ({snippets.length})
          </span>
        )}</>}
        headerRight={isAdmin && (
          <AddBtn onClick={() => setSnippetModal({ mode: 'create' })} size={13} title="New Snippet" />
        )}
      >
          {snippets.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic', margin: 0, padding: '4px 0' }}>
              No snippets yet. Create reusable text blocks for job ads.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {snippets.map(s => (
                <div key={s.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '9px 12px', borderRadius: 6,
                  background: '#F8F9FA', border: '1px solid var(--border)',
                }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{s.name}</span>
                  {isAdmin && (
                    <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexShrink: 0 }}>
                      <EditBtn onClick={() => setSnippetModal({ mode: 'edit', snippet: s })} title="Edit" />
                      <DelBtn onClick={() => handleDeleteSnippet(s)} title="Delete" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
      </CollapsibleLibrarySection>
    </div>
  );
};
